import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Bot,
  BotSeverity,
  Currency,
  CustomGridOrder,
  Direction,
  GridType,
  Ticker,
  TimePreset,
  TimePresetRender,
} from "../../_app/types";
import { GRID_AMOUNT, INIT_STATE_BOT, INIT_STATE_STRATEGY } from "../../_app/consts";

export interface TimeFrame {
  checked?: boolean;
  interval: string;
  assignedVolume: number;
  profitPercent: number;
}

export type PositionBehaviour = "Reduce" | "Both" | "Accumulate";

export interface Strategy {
  // post params
  id: string;
  displayedId: string;
  base: string;
  quote: string;
  ticker: Ticker;
  accountId: string;
  profileId: string;
  currencyType: string;
  investment: number;
  investmentQuote: number;
  requiredMargin: number;
  startPrice: number;
  minPrice: number;
  maxPrice: number;
  leverage: number;
  stepsCount: number;
  gridDirection: Direction;
  timeframes: TimeFrame[];
  customFrame: boolean;
  // get param
  gridProfit: number;
  fees: number;
  fundingPnl: number;
  realizedPnl: number;
  unrealizedPnl: number;
  currentOpenPositionAmount: number;
  avgPositionOpeningPrice: number;
  closeTime: string;
  marketType: string;
  status: BotSeverity;
  createdAt: string;
  presetType: TimePreset;
  runtimeHours: number;
  strategyPositionRealizedPnl: number;
  strategyPositionFees: number;
  totalCurrentPositionAmount: number;
  totalPositionAvgPrice: number;
  totalRealizedPnl: number;
  totalFees: number;
  unrealizedStrategyPositionPnl: number;
  totalUnrealizedPnl: number;
  currentStrategyPositionAmount: number;
  avgStrategyPositionPrice: number;
  startTime: string;
  apr: number;
  positionBehaviour: PositionBehaviour;
}

// Начальное состояние
const initialState: Strategy = INIT_STATE_STRATEGY;

const strategySlice = createSlice({
  name: "strategy",
  initialState,
  reducers: {
    setStrategyDirection: (state, action: PayloadAction<Direction>) => {
      state.gridDirection = action.payload;
    },
    setStrategyInvestment: (state, action: PayloadAction<number>) => {
      state.investment = action.payload;
    },
    setStrategyLeverage: (state, action: PayloadAction<number>) => {
      state.leverage = action.payload;
    },
    setStrategyBase: (state, action: PayloadAction<string>) => {
      state.base = action.payload;
    },
    setStrategyQuote: (state, action: PayloadAction<string>) => {
      state.quote = action.payload;
    },
    setStrategyMinPrice: (state, action: PayloadAction<number>) => {
      state.minPrice = action.payload;
    },
    setStrategyMaxPrice: (state, action: PayloadAction<number>) => {
      state.maxPrice = action.payload;
    },
    setStrategyStartPrice: (state, action: PayloadAction<number>) => {
      state.startPrice = action.payload;
    },
    setStrategyCurrencyType: (state, action: PayloadAction<Currency>) => {
      state.currencyType = action.payload;
    },
    setStrategyTicker: (state, action: PayloadAction<Ticker>) => {
      state.ticker = action.payload;
    },
    setStrategyStepsCount: (state, action: PayloadAction<number>) => {
      state.stepsCount = action.payload;
    },
    setCustomFrame: (state, action: PayloadAction<boolean>) => {
      state.customFrame = action.payload;
    },
    setBehaviour: (state, action: PayloadAction<PositionBehaviour>) => {
      state.positionBehaviour = action.payload;
    },
    clearStrategyState: (state) => {
      Object.assign(state, INIT_STATE_STRATEGY);
    },
  },
});

// Экспортируем экшены
export const {
  setStrategyDirection,
  setStrategyInvestment,
  setStrategyLeverage,
  setStrategyBase,
  setStrategyMaxPrice,
  setStrategyMinPrice,
  setStrategyCurrencyType,
  setStrategyTicker,
  setStrategyStepsCount,
  setStrategyQuote,
  setStrategyStartPrice,
  setCustomFrame,
  clearStrategyState,
  setBehaviour,
} = strategySlice.actions;

// Экспортируем редьюсер
export default strategySlice.reducer;
